import React from 'react';

type Padding = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
type Elevation = 0 | 1 | 2 | 3;
type Radius = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

interface CardProps extends React.HTMLAttributes<HTMLElement> {
  padding?: Padding;
  elevation?: Elevation;
  bordered?: boolean;
  radius?: Radius;
  /** Override the rendered tag — useful when a section semantic is needed
   *  (e.g. landmark sections in Settings). Defaults to 'div'. */
  as?: React.ElementType;
}

const paddingClass: Record<Padding, string> = {
  none: '',
  sm: 'p-2',
  md: 'p-3',
  lg: 'p-4',
  xl: 'p-5',
  '2xl': 'p-6',
};

const elevationClass: Record<Elevation, string> = {
  0: 'bg-surface-0',
  1: 'bg-surface-1',
  2: 'bg-surface-2',
  3: 'bg-surface-3',
};

const radiusClass: Record<Radius, string> = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};

export function Card({
  padding = 'md',
  elevation = 1,
  bordered = true,
  radius = 'xl',
  as: Tag = 'div',
  className,
  children,
  ...rest
}: CardProps) {
  const merged = [
    elevationClass[elevation],
    radiusClass[radius],
    paddingClass[padding],
    bordered && 'border border-border-subtle',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag {...rest} className={merged}>
      {children}
    </Tag>
  );
}
